// Server-side tunables (plan §17.4 `timers` + `net`, §13.7 chat cap) as typed config
// (invariant 6: no literal appears in server code). DELIBERATELY outside GameConfig and
// the §17.6 composed/hashed config JSON (D24): the ENGINE never reads any of these — a
// timeout-synthesized action is logged as an ordinary ACTION with origin:'timeout', so
// replay is timer-free by construction — and folding them into the composed JSON would
// re-shape the §11.2 log header and break the H1-frozen golden byte pin (T-LOG-005).
// Lives in shared (not server) so the net-smoke tool, client tests and the server all
// read ONE source; it is pure data, so every fence stays clean.

/**
 * The per-window budgets (H11). Every window in a turn works the same way and differs only
 * in how long it lasts — see `time/turnClock.ts` for the mechanic these numbers drive.
 */
export interface TurnBudgets {
  /** Shared by ACTION and every interlude the seat owns in that turn; armed AT the roll. */
  readonly turnMs: number;
  /** PRE_ROLL's own budget. Lapses ⇒ the dice roll themselves and the turn clock starts. */
  readonly rollMs: number;
  /** Where to found — the most consequential choice in the game. */
  readonly setupSettlementMs: number;
  /** The attached road — nearly forced, so it needs far less. */
  readonly setupRoadMs: number;
}

export interface TimerConfig extends TurnBudgets {
  readonly turnGraceMs: number;
  readonly responseMs: number;
  readonly responseGraceMs: number;
  readonly vacantStepMs: number;
  readonly expiriesToVacancy: number;
}

export interface NetConfig {
  readonly maxStrikes: number;
  readonly actRate: { readonly perSec: number; readonly burst: number };
  readonly chatRate: { readonly perSec: number; readonly burst: number };
  /**
   * H6-S3: map-bearing settings patches get their OWN bucket. Validating a paste costs a
   * full §6.7 parse plus the constructive setup-viability proof — ~23 ms on a legal 8-seat
   * map — and the shared frame budget (10/s) let one socket burn ~254 ms of CPU per second
   * without ever earning a strike, stalling the 250 ms service loop and the whole API.
   */
  readonly mapPasteRate: { readonly perSec: number; readonly burst: number };
  readonly payloadCaps: {
    readonly hello: number;
    readonly json: number; // act & every plain control envelope
    readonly jsonWithMap: number; // createRoom / setSettings carrying customMapJson
    readonly chat: number;
  };
  readonly roomCode: { readonly length: number; readonly alphabet: string };
  readonly perIpSessions: number;
  readonly connectPerMin: number;
  readonly failedJoinPerMin: number;
  readonly abandonMs: number;
  readonly unstartedTtlMs: number;
  /**
   * D217 (§13.8 backfill): how long a released-short room waits for the humans it was
   * promised before it turns the remaining seats into bots and starts anyway.
   *
   * It is NOT the same clock as the queue's own `backfillSec` — that one decides when to
   * give up on matchmaking; this one covers the walk from "formed" to "joined", which is a
   * websocket connect and a lobby render. A room that filled starts at once regardless.
   */
  readonly backfillJoinMs: number;
}

/** S5b bot seats: server-side drive-loop bounds (T-NET-035), plus the H11 personality
 *  dials (plan §13.9).
 *
 *  D117 — these live HERE and never in the hashed GameConfig. Bot behaviour is not a rule:
 *  it changes no legality and no outcome-given-actions. Folding it into the §17.6 composed
 *  JSON would re-shape the §11.2 log header and invalidate every rated game's config hash
 *  on a tuning pass, which is the same reasoning that put the timers here (D24). */
export interface BotConfig {
  /**
   * Hard cap on bot actions applied per drive burst. A burst runs from one trigger
   * (game start, a human act, a timer sweep) until no bot window is open — the cap
   * bounds the worst case (a bot-only table would otherwise play its WHOLE game inside
   * one call, starving the 250 ms service loop); tickAll resumes any burst it cut short.
   */
  readonly maxActionsPerBurst: number;
  /**
   * H11-S1: the largest score delta one dial at full swing may apply to an action kind.
   * Rungs are `BOT_RUNG` apart (100), so a dial CAN promote a settlement over a city —
   * that is the point of `tempo` — but only when the dial is far from neutral. At a
   * neutral disposition every delta is exactly 0 and the H10 ladder order is reproduced
   * verbatim, which is what T-ENG-084 pins.
   */
  readonly dispositionWeight: number;
  /**
   * H11-S1: how hard the dials pull SITE and ROAD choice. Measured cause: choosing between
   * action KINDS almost never comes up (59 of 118 windows offer only `endTurn`), so this —
   * not `dispositionWeight` — is where a character actually becomes visible. A site's raw
   * score steps ~8 per pip, so 8 here reorders adjacent sites at a moderate swing.
   */
  readonly siteWeight: number;
  /** H11-S3: sampling floor below the best score. Outside it the bot never draws, so a
   *  BAD move is unreachable however the draw falls (D120). */
  readonly wobbleBand: number;
  /** H11-S3: bounds for the score-spread-derived temperature. */
  readonly temperature: { readonly min: number; readonly max: number };
  /** H11-S2: how long a drift trigger (grudge, desperation) takes to decay back. */
  readonly driftDecayTurns: number;
  /** H11-S4: cap on generated player-trade offers per turn. */
  readonly offersPerTurn: number;
  /**
   * D215 (the user, playing it): "they end their turn too quickly for me to consider a
   * trade." A bot holding standing offers a HUMAN could still answer does not end its
   * turn until the newest of them has stood this long. Bot-only tables never wait — the
   * bystander pre-pass answers (or ignores) offers within a step, and a hold with no
   * human to hold FOR would just be dead air.
   */
  readonly offerConsiderMs: number;
  /** H11-S6: think-time pacing, always kept inside the seat's remaining turn clock. */
  readonly thinkMs: { readonly floor: number; readonly ceiling: number };
  /**
   * H11: the beat between one local bot action and its next. The pacing used to be the
   * tick interval itself (250 ms), which read as a machine flicking through its options
   * rather than a player taking a turn. One knob; raise it if bots still feel hurried.
   */
  readonly stepMs: number;
  /**
   * D192: HOW LONG A BOT TAKES OVER ONE ACTION, as a range rather than a constant.
   *
   * `stepMs` was 600 flat, and the user's report is the whole argument against it: "bots
   * play instantly… I cannot follow what they do". A fixed beat is also the tell — real
   * players are not metronomes. Every step draws a think time in [min, max], and now and
   * then (`ponderChance`) a bot stops to consider for `ponderMs` on top, so a turn has a
   * rhythm instead of a tick.
   *
   * The draw is DETERMINISTIC (hashed off the game seed and the step index), so a replay
   * paces identically and a test can assert the pacing rather than sample it. Timing is
   * presentation and never touches the log — no draw-order entry moves.
   */
  readonly think: {
    /** Routine actions — a roll, an end-turn. Quick, but never a blink. */
    readonly quick: { readonly minMs: number; readonly maxMs: number };
    /** Consequential ones — a placement, a buy, the bandit, a trade. The look-up moment. */
    readonly weighty: { readonly minMs: number; readonly maxMs: number };
    /** Chance in [0,1] that a step additionally pauses as if reconsidering. */
    readonly ponderChance: number;
    /** How long that pause adds, at most. */
    readonly ponderMs: number;
    /** Which action kinds are weighty. Everything else is quick. */
    readonly weightyKinds: readonly string[];
  };
  /**
   * H20-S10: how a bot reads the sea (engine/botSea.ts), in site-score units (8 per pip).
   * Search depth bounds the route it plans (and the work per decision); a step's cost is what
   * one more ship is worth giving up; `fogValue` is ONE unexplored cell beside a corner, as an
   * expected discovery — roughly 10/12 land at ~3.3 pips, plus the card finding it pays. The
   * first cut valued a CORNER at 24 flat, so fog more than two ships out was never worth a
   * voyage and bots never explored at all (measured: 0 reveals in 16 fog games).
   */
  readonly sea: { readonly searchDepth: number; readonly stepCost: number; readonly fogValue: number };
}

/**
 * H11 tempo presets (retention design §3): one place naming the clock speeds a table can
 * run at, so the menu, the lobby and the local solo driver never disagree.
 *
 * `rollMs` is a SEPARATE, shorter clock for PRE_ROLL — the thing Colonist has and we did
 * not. The turn clock covering the roll means a stalled roll eats the whole turn budget
 * while nobody can do anything; a short roll clock is what keeps a table moving.
 */
export interface SpeedRung extends TurnBudgets {
  readonly id: SpeedId;
  readonly label: string;
  /** The short caption under a slider rung, and the word the lobby summary uses. */
  readonly tick: string;
  /** One-line description shown under the label in the picker. */
  readonly blurb: string;
}

/**
 * D216: ONE speed vocabulary.
 *
 * The product used to name the same clock three different ways — the solo picker said
 * Relaxed/Standard/Blitz, the lobby slider said Ultra fast/Fast/Medium/Slow/Async for the
 * SAME five budgets, and quickmatch inherited the brief-B4 90 s default, which matched no
 * named pace anywhere. Blitz and "Ultra fast" were both 15 s; Standard and "Medium" were
 * both 60 s. A player could not carry a word from one screen to the next.
 *
 * The five `turnMs` values are exactly the wire's detent union (`settingsSchema.turnMs`),
 * so this is a NAMING unification and not a protocol change: no golden moves, no config
 * hash moves — timers live outside the §17.6 composed config by D24.
 *
 * ORDER IS LOAD-BEARING: fastest first, because the lobby's tiered slider is index-based
 * and rung 0 is the left end of its track.
 */
export type SpeedId = 'blitz' | 'fast' | 'standard' | 'relaxed' | 'mail';

export const SPEED_LADDER: readonly SpeedRung[] = Object.freeze([
  Object.freeze({ id: 'blitz' as const, label: 'Blitz', tick: '15s', blurb: 'Fifteen seconds. No dithering.', turnMs: 15_000, rollMs: 8_000, setupSettlementMs: 20_000, setupRoadMs: 10_000 }),
  Object.freeze({ id: 'fast' as const, label: 'Fast', tick: '30s', blurb: 'Thirty seconds. Keep it moving.', turnMs: 30_000, rollMs: 10_000, setupSettlementMs: 30_000, setupRoadMs: 14_000 }),
  Object.freeze({ id: 'standard' as const, label: 'Standard', tick: '1min', blurb: 'A minute a turn. The usual pace.', turnMs: 60_000, rollMs: 15_000, setupSettlementMs: 45_000, setupRoadMs: 20_000 }),
  Object.freeze({ id: 'relaxed' as const, label: 'Relaxed', tick: '2min', blurb: 'Two minutes a turn. Think it through.', turnMs: 120_000, rollMs: 30_000, setupSettlementMs: 90_000, setupRoadMs: 40_000 }),
  // The async rung. It is the same clock as every other one, just a very long one — which
  // is why it is a rung and not a mode. Its sub-budgets are proportionate rather than
  // token: a player answering by mail gets half a day to roll, not eight seconds.
  Object.freeze({ id: 'mail' as const, label: 'Mail', tick: '24h', blurb: 'A day a turn. Play from your inbox.', turnMs: 86_400_000, rollMs: 43_200_000, setupSettlementMs: 86_400_000, setupRoadMs: 43_200_000 }),
]);

/** Back-compat alias: a tempo IS a rung of the ladder, and always was. */
export type TempoPreset = SpeedRung;

/**
 * The rungs the SOLO picker offers, in the order it offers them (slowest first — the
 * picker reads as a menu, not a track). `mail` is deliberately absent: a day-per-turn
 * clock against three bots on this device is not a game, and `fast` is left out because
 * three rungs is a choice and five is a form.
 */
export const SOLO_SPEED_IDS: readonly SpeedId[] = Object.freeze(['relaxed', 'standard', 'blitz'] as const);

export const TEMPO_PRESETS: readonly TempoPreset[] = Object.freeze(
  SOLO_SPEED_IDS.map((id) => SPEED_LADDER.find((r) => r.id === id)!),
);

export const DEFAULT_TEMPO_ID = 'standard';

/** The turn budgets a room may be dialled to — the wire's own detent set, as a type. */
export type DetentMs = 15_000 | 30_000 | 60_000 | 120_000 | 86_400_000;

/**
 * The table sizes §13.8 matchmaking will form at. Shared because BOTH ends speak it: the
 * menu offers it and the queue matches on it, and a second copy of a wire vocabulary is a
 * copy that can disagree — which is exactly how the client came to send a hardcoded '3' at
 * a route that defaulted to '4'.
 */
export type SizePref = '2' | '3-4' | '5+';
export const SIZE_PREFS: readonly SizePref[] = Object.freeze(['2', '3-4', '5+'] as const);

/**
 * D288: the seat counts a table-size preference accepts, LARGEST first — the queue forms at the
 * biggest table the pool can fill and a bot backfill mints at the smallest (every unfilled
 * seat becomes a bot). '5+' runs to the engine's ceiling: it is always a SCALED board built for
 * whoever sits down, so there is no map whose range would cap it lower.
 */
export function seatsForSizePref(pref: SizePref): readonly number[] {
  if (pref === '2') return [2];
  if (pref === '3-4') return [4, 3];
  return [8, 7, 6, 5];
}

/** Does a table of `seats` satisfy this preference? (A public room is matched on its capacity.) */
export function sizePrefAccepts(pref: SizePref, seats: number): boolean {
  return seatsForSizePref(pref).includes(seats);
}

/**
 * D288: the pre-D288 vocabulary ('3', '4', '5-6') read onto the new one, so a client loaded
 * before the deploy — or a remembered pick in a browser — lands on the nearest new choice
 * instead of a refusal.
 */
export function sizePrefOf(raw: unknown): SizePref {
  if (raw === '2' || raw === '3-4' || raw === '5+') return raw;
  if (raw === '5-6') return '5+';
  return '3-4';
}

/**
 * D288: the board a quickmatch table is dealt on. The classic island seats 3–4; anything else
 * is the SCALED board, generated for the table that actually sits down (the user's rule for
 * 5+, and the D174 fit for two).
 */
export function quickmatchMapFor(seats: number): 'classic-random' | 'scaled' {
  return seats >= 3 && seats <= 4 ? 'classic-random' : 'scaled';
}

/**
 * The pace a §13.8 quickmatch table runs at. Pinned, and pinned to a NAMED rung — the
 * queue used to inherit whatever the deployment's turn budget happened to be (90 s in
 * production), so the one mode most new players meet ran at a speed with no name.
 *
 * The two constants are bound by a test rather than by the type system, because a `find`
 * over the ladder cannot produce a literal type. That test is the tripwire: change the
 * rung and the pin has to follow it.
 */
export const QUICKMATCH_SPEED_ID: SpeedId = 'standard';
export const QUICKMATCH_TURN_MS: DetentMs = 60_000;

/**
 * A rung's turn budget as the wire's own literal type. Written as a switch rather than a
 * `find`, because a lookup over the ladder returns `number` and the room settings want the
 * detent union — and a cast there would be the one place the two could silently diverge.
 */
export function detentFor(id: SpeedId): DetentMs {
  switch (id) {
    case 'blitz':
      return 15_000;
    case 'fast':
      return 30_000;
    case 'standard':
      return 60_000;
    case 'relaxed':
      return 120_000;
    case 'mail':
      return 86_400_000;
  }
}

/** A rung by id; unknown ids fall back to Standard rather than throwing at a menu. */
export function speedById(id: string): SpeedRung {
  return SPEED_LADDER.find((r) => r.id === id) ?? SPEED_LADDER.find((r) => r.id === DEFAULT_TEMPO_ID)!;
}

/** The rung a room's `turnMs` detent names, or null when it names none. */
export function speedByTurnMs(ms: number): SpeedRung | null {
  return SPEED_LADDER.find((r) => r.turnMs === ms) ?? null;
}

/**
 * D219: the one public statistic, and the rule that hides it.
 *
 * Shared because the client has to describe the same rule in its copy, and a second copy of
 * a threshold is a copy that can disagree (the `ranks.ts` argument, D198). The floor is a
 * PRIVACY control: at small N a game count beside a short leaderboard is close to naming
 * who played.
 */
export const PUBLIC_STATS = Object.freeze({ windowDays: 7, floor: 25, onlineFloor: 3 });

/** H11: how long a paying tile stays lit. Long enough to follow, short enough not to nag. */
export const HARVEST_FLASH_MS = 1_400;

/** H11: how long the big YOUR TURN sits over the board before it clears. */
export const TURN_FLASH_MS = 1_500;


/** H15-S3: the dark veil over a disc whose number came up under the bandit.
 *  D215 (the user, playing it): longer and darker — the receipt must READ, and with the
 *  near-miss ring deleted this is the roll's only scene effect. */
export const BLOCKED_VEIL_MS = 1_800;

/** H15: how long the build-gap toast stands after a roll that did NOT close the gap. */
export const TOAST_LINGER_MS = 4_000;

/** H15: how long the hype toast celebrates a closed build gap before fading. */
export const TOAST_HYPE_MS = 5_000;

/**
 * H11: how long a finished game's result stands before every player is returned.
 * A table that has finished should not need somebody to be the one who closes it.
 */
export const END_SCREEN_MS = 60_000;

/**
 * A tempo by id. Resolves against the WHOLE ladder, not the solo subset: a stored `fast`
 * must come back as Fast, and quietly returning Standard for a rung that plainly exists
 * is the shape of bug that hides for months.
 */
export function tempoById(id: string): TempoPreset {
  return speedById(id);
}

export interface ServerConfig {
  readonly timers: TimerConfig;
  readonly net: NetConfig;
  readonly chat: { readonly maxChars: number };
  readonly bots: BotConfig;
}

export const SERVER_CONFIG: ServerConfig = {
  timers: {
    turnMs: 90_000, // brief B4 default
    rollMs: 20_000, // H11: PRE_ROLL's own budget — the turn clock starts AT the roll
    setupSettlementMs: 60_000, // H11: the founding choice deserves more than the road
    setupRoadMs: 30_000,
    // The displayed countdown is the contract: 0:00 resolves on the next server sweep,
    // not thirty seconds later. Deployments may still opt into a small transport grace.
    turnGraceMs: 0,
    responseMs: 45_000, // OQ-3 (P0 sign-off)
    responseGraceMs: 0,
    vacantStepMs: 1_500,
    expiriesToVacancy: 2,
  },
  net: {
    maxStrikes: 3, // seed doctrine
    actRate: { perSec: 10, burst: 20 },
    chatRate: { perSec: 1, burst: 5 },
    mapPasteRate: { perSec: 0.2, burst: 3 }, // a few pastes a minute is generous for a human editing a map
    payloadCaps: {
      hello: 128,
      json: 8 * 1024,
      jsonWithMap: 64 * 1024,
      chat: 2 * 1024,
    },
    roomCode: { length: 4, alphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' }, // caps-unambiguous: no I/O/0/1
    perIpSessions: 8,
    connectPerMin: 60,
    failedJoinPerMin: 20, // seed D105
    abandonMs: 300_000, // seed D97
    unstartedTtlMs: 60_000,
    backfillJoinMs: 15_000, // a connect and a lobby render, generously
  },
  chat: { maxChars: 280 },
  bots: {
    maxActionsPerBurst: 25, // ~100 actions/s at the 250 ms cadence — instant turns, bounded slices
    dispositionWeight: 140, // 1.4 rungs at full swing (§13.9); 0 at a neutral disposition
    siteWeight: 8, // one pip-step of site score per unit swing — where character shows
    wobbleBand: 60, // H11-S3
    temperature: { min: 0.01, max: 45 }, // H11-S3
    driftDecayTurns: 6, // H11-S2
    offersPerTurn: 2, // H11-S4
    offerConsiderMs: 8_000, // D215: a human gets a real look at a standing offer
    thinkMs: { floor: 300, ceiling: 2_600 }, // H11-S6
    stepMs: 600, // the FLOOR between steps; `think` is what actually paces a bot (D192)
    think: {
      // TWO BANDS, not one range with a multiplier. The first cut was a 1.1-2.4 s range
      // scaled 1.45x for weighty actions, and the bands overlapped almost completely —
      // a placement on a low draw came out the same length as an end-turn on a high one,
      // so consecutive steps kept landing within 30 ms of each other. Varied numbers, and
      // still a metronome to watch. Separating the bands is what produces a RHYTHM: brisk
      // through the routine, a real pause on the thing worth looking at.
      quick: { minMs: 800, maxMs: 1_500 },
      weighty: { minMs: 1_900, maxMs: 3_300 },
      ponderChance: 0.18,
      ponderMs: 1_600,
      // The actions a human would look up for. An END_TURN needs no deliberation, and a bot
      // that paused over one would read as broken rather than thoughtful.
      //
      // EXACT kinds, spelled as the modules register them (D259). `botThinkMs` matches by
      // equality, and the first list named 'BUILD', 'SETUP_PLACE' and 'ROB_PLAYER' — none of
      // them an action kind — so every bot build, placement and steal ran in the quick band
      // while its spec passed by feeding in the same made-up 'BUILD'. T-ENG-092 now refuses a
      // name no module registers, and makes every new action kind declare its band.
      weightyKinds: [
        'SETUP_SETTLEMENT', 'SETUP_ROAD', // a placement…
        'BUILD_ROAD', 'BUILD_SETTLEMENT', 'BUILD_CITY', 'ROADWORKS_PLACE', // …the engine narrates a card's road as BUILT too
        'BUY_ADVANCE', 'PLAY_ADVANCE', // a buy, a play
        'MOVE_BANDIT', 'STEAL_PICK', // the bandit
        'OFFER_TRADE', 'ACCEPT_TRADE', 'RATIFY_TRADE', 'SUPPLY_TRADE', // a trade — RATIFY is the moment the cards move
        // H20 (D274): the Open Sea's own. A ship at setup or a ship built is a placement (the
        // engine narrates SETUP_PLACED / BUILT for both), and the pirate is the bandit's twin
        // (PIRATE_MOVED). A gold choice and a ship move stay routine — T-ENG-092 says why.
        'SETUP_SHIP', 'BUILD_SHIP', 'MOVE_PIRATE',
      ],
    },
    // H20-S10 (measured, D269): ten ships of look-ahead reach round a home island to the far
    // strait (six left a seat on the frame side blind to the fog); a step costs about a pip of
    // production; an unexplored cell is worth about one average land hex (most of the fog is land,
    // and finding it pays a card)
    sea: { searchDepth: 10, stepCost: 10, fogValue: 30 },
  },
} as const;
